'use client';

import { API_ENDPOINTS, getAuthHeaders } from '@/lib/api';
import { useEffect, useState } from 'react';

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'create' | 'edit';
  projectId?: number;
  onProjectCreated?: () => void;
}

export default function ProjectModal({ isOpen, onClose, mode = 'create', projectId, onProjectCreated }: ProjectModalProps) {
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [projectType, setProjectType] = useState<0 | 1>(0); // 0 = Product Development, 1 = Business Operations
  const [teamMembers, setTeamMembers] = useState<Array<{
    id: number;
    member_id: number;
    full_name: string;
    email: string;
    access: number; // 0 = Can Edit, 1 = Can View
    avatar: string;
    isCreator?: boolean; // Flag to identify the creator
  }>>([]);
  const [currentUserMemberId, setCurrentUserMemberId] = useState<number | null>(null);
  const [addMemberEmail, setAddMemberEmail] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetching, setFetching] = useState(false);

  // Load project data when editing or load current user when creating
  useEffect(() => {
    if (isOpen && mode === 'edit' && projectId) {
      fetchProjectData();
    } else if (isOpen && mode === 'create') {
      // Reset form for create mode
      setProjectName('');
      setDescription('');
      setProjectType(0);
      setTeamMembers([]);
      setCurrentUserMemberId(null);
      setError('');
      // Load current user as team member
      loadCurrentUserMember();
    }
  }, [isOpen, mode, projectId]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  const loadCurrentUserMember = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.PROJECTS.CURRENT_USER_MEMBER, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const member = await response.json();
        const currentUserMember = {
          id: Date.now(), // Temporary ID for UI
          member_id: member.id,
          full_name: member.full_name || member.email,
          email: member.email,
          access: 0, // Can Edit (default for creator)
          avatar: (member.full_name || member.email || 'U').charAt(0).toUpperCase(),
          isCreator: true, // Mark as creator
        };
        setCurrentUserMemberId(member.id);
        setTeamMembers([currentUserMember]);
      }
    } catch (error) {
      // Failed to load current user member
    }
  };

  const fetchProjectData = async () => {
    if (!projectId) return;
    
    try {
      setFetching(true);
      const response = await fetch(API_ENDPOINTS.PROJECTS.DETAIL(projectId), {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const project = await response.json();
        setProjectName(project.name);
        setDescription(project.requirements || '');
        setProjectType(project.type);
        
        // Load team members
        if (project.members && Array.isArray(project.members)) {
          // Find the creator (member who matches created_by)
          const creatorMemberId = project.created_by?.id;
          
          const members = project.members.map((m: { id: number; member: { id: number; full_name?: string; email: string }; access?: number }) => ({
            id: m.id,
            member_id: m.member.id,
            full_name: m.member.full_name || m.member.email,
            email: m.member.email,
            access: m.access || 0,
            avatar: (m.member.full_name || m.member.email || 'U').charAt(0).toUpperCase(),
            isCreator: m.member.id === creatorMemberId,
          }));
          setTeamMembers(members);
          
          // Set current user member ID if they're in the list
          const currentUserMember = members.find((m: { isCreator: boolean; member_id: number }) => m.isCreator);
          if (currentUserMember) {
            setCurrentUserMemberId(currentUserMember.member_id);
          }
        }
      } else {
        setError('Failed to load project data');
      }
    } catch (error) {
      setError('Unable to load project data');
    } finally {
      setFetching(false);
    }
  };

  if (!isOpen) return null;

  const handleSaveProject = async () => {
    if (!projectName.trim()) {
      setError('Project name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const url = mode === 'edit' && projectId
        ? API_ENDPOINTS.PROJECTS.DETAIL(projectId)
        : API_ENDPOINTS.PROJECTS.LIST;
      
      const method = mode === 'edit' ? 'PATCH' : 'POST';

      // Prepare request body
      const requestBody: Record<string, unknown> = {
        name: projectName,
        requirements: description || '',
        type: projectType,
      };

      // Only include members when editing (backend automatically adds creator when creating)
      if (mode === 'edit') {
        const membersData = teamMembers.map(m => ({
          member_id: m.member_id,
          access: m.access,
        }));
        requestBody.members = membersData;
      }

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        await response.json();

        // If creating, the backend automatically adds the creator as a member
        // If editing, members are already included in the response
        
        // Reset form
        setProjectName('');
        setDescription('');
        setProjectType(0);
        setTeamMembers([]);
        setCurrentUserMemberId(null);
        setError('');
        onClose();
        
        // Notify parent to refresh project list
        if (onProjectCreated) {
          onProjectCreated();
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || errorData.message || `Failed to ${mode === 'edit' ? 'update' : 'create'} project`);
      }
    } catch (err) {
      setError('Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = (memberId: number) => {
    // Don't allow removing the creator
    if (memberId === currentUserMemberId) {
      setError('Cannot remove the project creator');
      return;
    }
    setTeamMembers(prev => prev.filter(m => m.member_id !== memberId));
  };

  const handleAddMember = async () => {
    if (!addMemberEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    // Check if member already added
    if (teamMembers.some(m => m.email.toLowerCase() === addMemberEmail.toLowerCase())) {
      setError('This member is already added to the project');
      return;
    }

    // Check if trying to add current user (they're already the creator)
    if (currentUserMemberId) {
      // We'll check this after fetching the member info
    }

    try {
      setAddingMember(true);
      setError('');
      
      const response = await fetch(`${API_ENDPOINTS.PROJECTS.SEARCH_MEMBER}?email=${encodeURIComponent(addMemberEmail)}`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const member = await response.json();
        
        // Check if this is the current user (creator)
        if (currentUserMemberId && member.id === currentUserMemberId) {
          setError('You are already the project creator');
          return;
        }
        
        const newMember = {
          id: Date.now(), // Temporary ID for UI
          member_id: member.id,
          full_name: member.full_name || member.email,
          email: member.email,
          access: 0, // Default to Can Edit
          avatar: (member.full_name || member.email || 'U').charAt(0).toUpperCase(),
        };
        setTeamMembers(prev => [...prev, newMember]);
        setAddMemberEmail('');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'User with this email not found');
      }
    } catch (err) {
      setError('Unable to search for member. Please try again.');
    } finally {
      setAddingMember(false);
    }
  };

  const handleRoleChange = (memberId: number, access: number) => {
    // Don't allow changing creator's access (they must always be "Can Edit")
    if (memberId === currentUserMemberId && access !== 0) {
      setError('Project creator must have "Can Edit" access');
      return;
    }
    setTeamMembers(prev => prev.map(m => 
      m.member_id === memberId ? { ...m, access } : m
    ));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-300/50 backdrop-blur-sm">
      <div className="bg-[#0f1429] border border-[#1a1f3a] rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#1a1f3a]">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-white tracking-tight">
              {mode === 'create' ? 'Create New Project' : 'Edit Project'}
            </h2>
          </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-300 transition-colors"
            >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Subtitle */}
          <p className="text-gray-500 text-sm font-normal">
            {mode === 'create' ? 'Set up a new project workspace' : 'Edit project details'}
          </p>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-md p-3 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Loading state when fetching project data */}
          {fetching && (
            <div className="text-gray-400 text-sm">Loading project data...</div>
          )}

          {/* Project Details */}
          <div className="flex gap-6">
            {/* Icon Section */}
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">Icon</label>
              <div className="w-20 h-20 bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg flex items-center justify-center">
                <div className="text-3xl">🚀</div>
              </div>
            </div>

            {/* Project Name and Description */}
            <div className="flex-1 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">Project Name</label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Enter project name"
                  className="w-full bg-[#1a1f3a] border border-[#2d1b3d] rounded-md px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">
                  Description <span className="text-gray-500 normal-case">(optional)</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of this project..."
                  rows={3}
                  className="w-full bg-[#1a1f3a] border border-[#2d1b3d] rounded-md px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 resize-none"
                />
              </div>
            </div>
          </div>


          {/* Team Members */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-3 uppercase tracking-wide">Team Members</label>
            <div className="space-y-2 mb-4">
              {teamMembers.length > 0 ? (
                teamMembers.map((member) => (
                  <div
                    key={member.member_id}
                    className="flex items-center gap-3 p-2.5 bg-[#1a1f3a] border border-[#2d1b3d] rounded-md"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold ${
                      member.avatar === 'R' ? 'bg-blue-600' : member.avatar === 'S' ? 'bg-green-600' : 'bg-purple-600'
                    }`}>
                      {member.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium text-sm">{member.full_name}</div>
                      <div className="text-xs text-gray-500 truncate">{member.email}</div>
                    </div>
                    <select
                      value={member.access}
                      onChange={(e) => handleRoleChange(member.member_id, parseInt(e.target.value))}
                      disabled={member.isCreator || member.member_id === currentUserMemberId}
                      className="bg-[#0f1429] border border-[#2d1b3d] rounded-md px-2.5 py-1.5 text-white text-xs focus:outline-none focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value={0}>Can Edit</option>
                      <option value={1}>Can View</option>
                    </select>
                    {member.isCreator || member.member_id === currentUserMemberId ? (
                      <div className="w-4 h-4 flex-shrink-0" title="Project creator cannot be removed" />
                    ) : (
                      <button
                        onClick={() => handleRemoveMember(member.member_id)}
                        className="text-gray-500 hover:text-red-400 transition-colors flex-shrink-0"
                        title="Remove member"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-gray-500 text-sm py-2">No team members added yet</div>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="email"
                value={addMemberEmail}
                onChange={(e) => setAddMemberEmail(e.target.value)}
                placeholder="Enter email address of existing user..."
                className="flex-1 bg-[#1a1f3a] border border-[#2d1b3d] rounded-md px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
                onKeyPress={(e) => e.key === 'Enter' && handleAddMember()}
                disabled={addingMember}
              />
              <button
                onClick={handleAddMember}
                disabled={addingMember || !addMemberEmail.trim()}
                className="px-3 py-2 bg-[#1a1f3a] border border-[#2d1b3d] hover:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md text-sm transition-colors"
              >
                {addingMember ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-[#1a1f3a]">
          <p className="text-xs text-gray-500">All fields can be changed later</p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-[#1a1f3a] border border-[#2d1b3d] hover:border-gray-600 text-white rounded-md text-sm font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveProject}
              disabled={loading || fetching}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium transition-colors"
            >
              {loading
                ? (mode === 'edit' ? 'Updating...' : 'Creating...')
                : (mode === 'edit' ? 'Update Project' : '+ Create Project')
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

